import {
  type MetaFunction,
  Form,
  Link,
  useLoaderData,
  type ActionFunctionArgs,
  redirect,
  data,
  type LoaderFunctionArgs,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { createEnrollmentForUser } from '#app/controls/users.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { type LocalizedContent } from '#app/utils/i18n.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.js'

interface TrainingProgram {
  _id: string
  title: LocalizedContent
}

export async function loader({ params }: LoaderFunctionArgs) {
  requireControlsToBeEnabled()

  const user = await prisma.user.findUniqueOrThrow({
    where: { id: params.id },
    select: {
      id: true,
      email: true,
    },
  })

  const trainingProgramQuery = `*[_type == 'trainingProgram']{_id, title} | order(title.en)`
  const trainingProgramResult = await executeQuery(trainingProgramQuery)

  if (trainingProgramResult.status !== Status.Success) {
    throw data(
      {
        title: 'Error',
        description: 'There was an error fetching the training programs',
      },
      { status: 500 },
    )
  }

  const trainingPrograms = trainingProgramResult.data as TrainingProgram[]

  return {
    user,
    trainingPrograms,
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  const userId = formData.get('userId')
  invariantResponse(typeof userId === 'string', 'User ID must be a string')

  const trainingProgramId = formData.get('trainingProgramId')
  invariantResponse(
    typeof trainingProgramId === 'string',
    'Training program ID must be a string',
  )

  await createEnrollmentForUser(userId, trainingProgramId)

  return redirect('/controls/users')
}

export default function CreateUserEnrollmentControlRoute() {
  const { user, trainingPrograms } = useLoaderData<typeof loader>()

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            <Link to={'/controls/users'}>Users</Link>
          </span>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            Create enrollment
          </span>
        </li>
      </ul>
      <dl>
        <dt className="mt-6 font-bold">User ID</dt>
        <dd>{user.id}</dd>
        <dt className="mt-6 font-bold">Email</dt>
        <dd>{user.email}</dd>
      </dl>
      <hr className="mt-6" />
      <Form className="py-6" method="POST">
        <fieldset className="mb-6">
          <legend>Select a training program:</legend>
          {trainingPrograms.map(trainingProgram => {
            return (
              <div key={trainingProgram._id}>
                <input
                  className="mr-2"
                  type="radio"
                  id={trainingProgram._id}
                  name="trainingProgramId"
                  value={trainingProgram._id}
                />
                <label htmlFor={trainingProgram._id}>
                  {trainingProgram.title.en}
                </label>
              </div>
            )
          })}
        </fieldset>

        <input type="hidden" name="userId" value={user.id} />

        <Button type="submit">Submit</Button>
      </Form>
    </>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: `Controls > Create enrollment | ${siteTitle}` }]
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        500: ({ error }) => (
          <div className="text-center">
            <h1>{error.data.title}</h1>
            <p className="text-3 mt-2 font-normal">{error.data.description}</p>
          </div>
        ),
      }}
    />
  )
}
